/**
 * Reading a field off a record nobody has checked.
 *
 * A save is JSON that came back from a browser store, which means every value is `unknown` and the
 * only honest way to a typed field is to look. These are the four looks the profile reader needs,
 * each returning a result rather than throwing, and each naming the path it was standing on so a
 * failure says `inventory.carried.stacks[2].defId` rather than `malformed`.
 *
 * The path is built by the caller as it descends. That is cheaper than it looks: a save is read
 * once, on load, and a defect nobody can locate costs far more than the strings do.
 */

import { refused, saved } from '@/save/saveResult';
import type { SaveResult } from '@/save/saveResult';

export type UnknownRecord = Readonly<Record<string, unknown>>;

/** An object, and not an array or null, either of which `typeof` calls an object. */
export function asRecord(value: unknown, path: string): SaveResult<UnknownRecord> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return refused({ reason: 'malformed', field: path });
  }
  return saved(value as UnknownRecord);
}

export function asArray(value: unknown, path: string): SaveResult<readonly unknown[]> {
  if (!Array.isArray(value)) return refused({ reason: 'malformed', field: path });
  return saved(value);
}

export function asString(value: unknown, path: string): SaveResult<string> {
  if (typeof value !== 'string') return refused({ reason: 'malformed', field: path });
  return saved(value);
}

/** Finite on purpose: `NaN` and both infinities are numbers, and none of them is a weight. */
export function asNumber(value: unknown, path: string): SaveResult<number> {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return refused({ reason: 'malformed', field: path });
  }
  return saved(value);
}

/** A number or an explicit null, which is how an absent instance id is written. */
export function asNullableNumber(value: unknown, path: string): SaveResult<number | null> {
  if (value === null) return saved(null);
  return asNumber(value, path);
}
